import { Link } from '@tanstack/react-router';
import { Badge, SectionLabel, StatCard } from '@wundo/ui';
import { ChevronRight } from 'lucide-react';
import type { DashboardData } from '@/lib/dashboard.ts';
import { formatSessionLength } from './sessionLength.ts';

export interface DashboardMetricsProps {
  data: DashboardData;
  /** Set when one cohort is being looked at rather than the whole organisation. */
  cohortId?: string;
}

/**
 * The dashboard figures, in the three groups the Vue console grouped them in:
 * who is here, what they do, and what came out of it.
 */
export function DashboardMetrics({ data, cohortId }: DashboardMetricsProps) {
  return (
    <div className='flex flex-col gap-8'>
      <section className='flex flex-col gap-4'>
        <SectionLabel as='h2'>Overview</SectionLabel>
        <div className='grid grid-cols-2 gap-4 max-agenda:grid-cols-1'>
          <StatCard label='Total users' value={data.total_users} />
          {/*
           * One cohort has a track or it does not; the whole organisation has a
           * number of them. Same figure, two different questions.
           */}
          {cohortId ? (
            <StatCard
              label='Track status'
              value={
                <Badge tone={data.active_tracks > 0 ? 'success' : 'neutral'}>
                  {data.active_tracks > 0 ? 'Active' : 'Inactive'}
                </Badge>
              }
            />
          ) : (
            <StatCard label='Active tracks' value={data.active_tracks} />
          )}
        </div>
      </section>

      <section className='flex flex-col gap-4'>
        <SectionLabel as='h2'>Activity</SectionLabel>
        <div className='grid grid-cols-2 gap-4 max-agenda:grid-cols-1'>
          <StatCard label='Total active sessions' value={data.total_sessions} />
          <StatCard
            label='Total active session length'
            value={formatSessionLength(data.total_session_duration_minutes)}
          />
        </div>
      </section>

      <section className='flex flex-col gap-4'>
        <SectionLabel as='h2'>Transfer</SectionLabel>
        <div className='grid grid-cols-3 gap-4 max-agenda:grid-cols-1'>
          {/*
           * The one figure on this page that leads somewhere: the questions
           * overview is the same count broken down by leadership domain.
           */}
          <Link
            to='/dashboard/questions'
            search={{ cohort: cohortId }}
            className='block rounded-lg'
          >
            <StatCard
              label='Coaching questions'
              value={data.coaching_questions}
              className='h-full transition hover:shadow-hover'
              hint={
                <span className='inline-flex items-center gap-0.5'>
                  View details
                  <ChevronRight className='size-3' aria-hidden='true' />
                </span>
              }
            />
          </Link>
        </div>
      </section>
    </div>
  );
}
